import * as React from "react";
import type { Initiative, Person } from "@/lib/types";
import { TEAMS, ALL_TAGS, tagNum, QUARTERS, DEFAULT_FY, fmtDate, toDate, normalizeQuarterId, quarterLabel } from "@/lib/constants";
import { byStart } from "@/lib/ordering";
import { Avatar, StatusPill } from "./primitives";

// Read-only table grouped by quarter, rows in chronological order (earliest
// start first). Manual drag-to-reorder was removed 2026-07-31 — with target
// dates required, chronology is the one ordering and it maintains itself.
// Change an initiative's quarter by editing its target date (drawer) or
// sliding its bar (timeline).
export function ListView({
  initiatives,
  roster,
  onRowClick,
  showPod = false,
}: {
  initiatives: Initiative[];
  roster: Person[];
  onRowClick: (i: Initiative) => void;
  /** Show the pod under the team name — on when a pod filter is active. */
  showPod?: boolean;
}) {
  const groups = React.useMemo(() => {
    // All quarters across every fiscal year, chronological. The current FY's
    // quarters always show (even empty — the year's shape matters); other
    // years' quarters appear once something lives in them.
    const known = QUARTERS.map(q => ({
      q: q.id,
      label: `${q.year} ${q.label} · ${q.months.join(" ")}`,
      items: initiatives.filter(i => normalizeQuarterId(i.targetQ) === q.id).sort(byStart),
      alwaysShow: q.year === DEFAULT_FY,
    })).filter(g => g.alwaysShow || g.items.length > 0);
    const stray = initiatives.filter(i => !QUARTERS.some(q => q.id === normalizeQuarterId(i.targetQ))).sort(byStart);
    return stray.length ? [...known, { q: "other", label: "Other", items: stray }] : known;
  }, [initiatives]);

  if (initiatives.length === 0) {
    return (
      <div className="list-table">
        <div className="empty-shell">
          <h3>No initiatives match your filters.</h3>
          <p>Adjust the filter chips above, or add a new initiative.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-table">
      <table>
        <thead>
          <tr>
            <th>Initiative</th>
            <th>Team</th>
            <th style={{ width: 160 }}>Tags</th>
            <th>DRI</th>
            <th>Target</th>
            <th>Status</th>
          </tr>
        </thead>
        {groups.map(group => (
          <tbody key={group.q}>
            <tr className="q-section-row">
              <td colSpan={6}>
                <span className="q-section-label">{group.label}</span>
                <span className="q-section-count">
                  {group.items.length} {group.items.length === 1 ? "initiative" : "initiatives"}
                </span>
              </td>
            </tr>
            {group.items.length === 0 && (
              <tr className="q-empty-row">
                <td colSpan={6}>No initiatives yet.</td>
              </tr>
            )}
            {group.items.map(i => {
              const team = TEAMS.find(t => t.id === i.teamId);
              const pod = showPod ? team?.pods.find(p => p.id === i.podId) : undefined;
              const dri = roster.find(p => p.id === i.driId);
              const pillars = i.pillarIds
                .map(pid => ALL_TAGS.find(p => p.id === pid))
                .filter((p): p is NonNullable<typeof p> => Boolean(p));
              const target = toDate(i.target);
              return (
                <tr
                  key={i.id}
                  onClick={() => onRowClick(i)}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${i.name}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onRowClick(i);
                    }
                  }}
                >
                  <td className="name-cell">
                    {i.name}
                    {i.tldr && <span className="tldr-row">{i.tldr}</span>}
                  </td>
                  <td>
                    {team?.name ?? "—"}
                    {pod && <span className="pod-row">{pod.name}</span>}
                  </td>
                  <td>
                    {pillars.length === 0 ? (
                      <span style={{ color: "var(--muted-2)", fontStyle: "italic", fontSize: 12 }}>—</span>
                    ) : (
                      <div className="pillar-chips">
                        {pillars.map(p => (
                          <span key={p.id} className="pillar-chip" title={`${p.name}`}>
                            {tagNum(p) && <span className="pillar-chip-num">{tagNum(p)}</span>}
                            <span className="pillar-chip-label">{p.short}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {dri ? (
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Avatar person={dri} size="sm" /> {dri.name}
                      </span>
                    ) : <span style={{ color: "var(--muted-2)" }}>—</span>}
                  </td>
                  <td>{quarterLabel(i.targetQ)}{target ? ` · ${fmtDate(target)}` : ""}</td>
                  <td><StatusPill status={i.status} /></td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}
